import { Eye, Loader2, Plus, Trash2 } from "lucide-react";
import FileTypeBadge from "@/components/FileTypeBadge";
import { MediaLightbox } from "@/components/AttachmentPreviews";
import { useRef, useState } from "react";
import { chiNotify } from "@/components/ChiNotification";
import { crmLabelStyle } from "@/components/crmFormStyles";
import { trpc } from "@/lib/trpc";
import {
  EVENT_DOCUMENT_CONTENT_TYPE,
  MAX_EVENT_DOCUMENT_BYTES,
  MAX_EVENT_DOCUMENT_LABEL,
  eventDocumentViewHref,
  formatEventDocumentSize,
} from "@shared/eventDocuments";

/**
 * The documents attached to an event, edited from the CRM's Media tab.
 *
 * Not the image uploader. That component crops and rasterises — a PDF dropped on
 * it becomes a picture of its first page, which is what an administrator got when
 * they tried to attach a slide deck this way. Documents are kept whole, stored the
 * moment they are uploaded (like event art, and unlike the draft fields that wait
 * for "Save changes"), and removed with one click.
 *
 * Titles are public — the event page shows them to everyone as a reason to become
 * a member — and downloads are for paid members only. Both facts are said here so
 * the person attaching a file knows who will see what.
 */
export default function EventDocumentsEditor({ eventSlug }: { eventSlug: string }) {
  const utils = trpc.useUtils();
  const documentsQuery = trpc.events.publicDocuments.useQuery({ eventSlug }, { retry: false });
  const refresh = () => utils.events.publicDocuments.invalidate({ eventSlug });
  const upload = trpc.events.uploadDocument.useMutation({ onSuccess: refresh });
  const remove = trpc.events.removeDocument.useMutation({ onSuccess: refresh });
  const [inFlight, setInFlight] = useState<string[]>([]);
  const [viewing, setViewing] = useState<{ id: number; title: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function attach(files: FileList | null) {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      if (!isPdf) {
        chiNotify.error("Not attached", { description: `${file.name} is not a PDF. Export the document to PDF and try again.` });
        continue;
      }
      if (file.size > MAX_EVENT_DOCUMENT_BYTES) {
        chiNotify.error("Not attached", { description: `${file.name} is larger than ${MAX_EVENT_DOCUMENT_LABEL}.` });
        continue;
      }

      setInFlight(current => [...current, file.name]);
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.addEventListener("load", () => resolve(String(reader.result)));
          reader.addEventListener("error", () => reject(new Error("That file could not be read.")));
          reader.readAsDataURL(file);
        });
        const stored = await upload.mutateAsync({ slug: eventSlug, fileName: file.name, dataUrl });
        chiNotify.success("Document attached", { description: `${stored.title} is on the event's Media tab for paid members.` });
      } catch (error) {
        chiNotify.error("Document not attached", {
          description: error instanceof Error ? error.message : "That file could not be uploaded.",
        });
      } finally {
        setInFlight(current => current.filter(name => name !== file.name));
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  const documents = documentsQuery.data ?? [];

  return (
    <section
      aria-labelledby="event-documents-heading"
      style={{ gridColumn: "1 / -1", display: "grid", gap: ".8rem", padding: "1rem", borderRadius: ".75rem", border: "1px solid rgba(0,212,255,.16)", background: "rgba(0,212,255,.035)" }}
    >
      <div>
        <p id="event-documents-heading" style={{ ...crmLabelStyle, marginBottom: ".3rem" }}>Event Docs</p>
        <p style={{ margin: 0, color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
          Slides, handouts and worksheets as PDF, up to {MAX_EVENT_DOCUMENT_LABEL} each. Everyone sees the titles on the event page; only paid members can download them.
        </p>
      </div>

      <div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          aria-label="Choose PDF documents to attach"
          onChange={event => void attach(event.target.files)}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={inFlight.length > 0}
          style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", minHeight: 36, padding: ".4rem .8rem", borderRadius: ".5rem", border: "1px solid rgba(0,212,255,.35)", background: "rgba(0,212,255,.1)", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: inFlight.length > 0 ? "wait" : "pointer" }}
        >
          {inFlight.length > 0 ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
          {inFlight.length > 0 ? `Attaching ${inFlight.length === 1 ? inFlight[0] : `${inFlight.length} files`}…` : "Attach PDF"}
        </button>
      </div>

      {documentsQuery.isLoading ? (
        <p style={{ margin: 0, color: "#94A3B8", fontSize: "var(--chi-body-compact)" }}>Loading documents…</p>
      ) : documentsQuery.isError ? (
        <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontSize: "var(--chi-body-compact)" }}>{documentsQuery.error.message}</p>
      ) : documents.length === 0 ? (
        <p style={{ margin: 0, color: "#718096", fontSize: "var(--chi-body-compact)" }}>No documents attached yet.</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".45rem" }}>
          {documents.map(document => (
            <li
              key={document.id}
              style={{ display: "flex", alignItems: "center", gap: ".65rem", padding: ".55rem .7rem", borderRadius: ".55rem", border: "1px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.03)" }}
            >
              <FileTypeBadge fileName={document.fileName} size={24} />
              <span style={{ minWidth: 0, flex: "1 1 auto", display: "grid" }}>
                <span style={{ color: "#F1F5F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{document.title}</span>
                <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "calc(var(--chi-body-compact) * 0.9)" }}>
                  {document.fileName} · {formatEventDocumentSize(document.sizeBytes)}
                </span>
              </span>
              <button
                type="button"
                aria-label={`View ${document.title}`}
                onClick={() => setViewing(document)}
                style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", minHeight: 32, padding: ".3rem .6rem", borderRadius: ".45rem", border: "1px solid rgba(0,212,255,.3)", background: "rgba(0,212,255,.08)", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
              >
                <Eye size={13} aria-hidden="true" /> View
              </button>
              <button
                type="button"
                aria-label={`Remove ${document.title}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate({ id: document.id }, {
                  onSuccess: () => chiNotify.success("Document removed", { description: `${document.title} is no longer on the event page.` }),
                  onError: error => chiNotify.error("Document not removed", { description: error.message }),
                })}
                style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", minHeight: 32, padding: ".3rem .6rem", borderRadius: ".45rem", border: "1px solid rgba(248,113,113,.3)", background: "rgba(248,113,113,.07)", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: remove.isPending ? "wait" : "pointer" }}
              >
                <Trash2 size={13} aria-hidden="true" /> Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {viewing && (
        <MediaLightbox
          attachment={{ url: eventDocumentViewHref(viewing.id), label: viewing.title, contentType: EVENT_DOCUMENT_CONTENT_TYPE }}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}
